import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { DeliveryMethod } from '@features/cart-item/types/delivery-method.type';
import { FieldsetBaseType, InputBaseType } from '@rd/form-builder/types';
import { PhysicalGiftCardFieldsMaxLength, User } from '@rd/types';
import { NydusRecipient } from '@rd/types/nydus-recipient.type';
import {
  LATIN_NAME_VALIDATION_REGEX,
  MOBILE_NUMBER_VALIDATION_REGEX,
  ONLY_US_ASCII_CHARACTERS,
  SPACE_ONLY_REGEX,
} from '@rd/utils/regex.utils';

@Injectable()
export class RecipientDetailFormService {
  private formFields: Record<string, InputBaseType> = {
    firstName: {
      className: 'recipient-first-name',
      key: 'firstName',
      labelTranslation: {
        label: 'gift_card.checkout.label.recipient_first_name',
        emptyErrorLabel: 'gift_card.checkout.error.recipient_first_name_empty',
        invalidPatternErrorLabel:
          'gift_card.checkout.error.recipient_first_name_invalid_pattern',
      },
      requiredOnSubmit: true,
      maxLength: PhysicalGiftCardFieldsMaxLength.FIRST_NAME,
      syncValidators: [
        Validators.pattern(LATIN_NAME_VALIDATION_REGEX),
        Validators.pattern(SPACE_ONLY_REGEX),
      ],
    },
    lastName: {
      className: 'recipient-last-name',
      key: 'lastName',
      labelTranslation: {
        label: 'gift_card.checkout.label.recipient_last_name',
        emptyErrorLabel: 'gift_card.checkout.error.recipient_last_name_empty',
        invalidPatternErrorLabel:
          'gift_card.checkout.error.recipient_last_name_invalid_pattern',
      },
      requiredOnSubmit: true,
      maxLength: PhysicalGiftCardFieldsMaxLength.LAST_NAME,
      syncValidators: [
        Validators.pattern(LATIN_NAME_VALIDATION_REGEX),
        Validators.pattern(SPACE_ONLY_REGEX),
      ],
    },
    companyName: {
      className: 'recipient-company-name',
      key: 'companyName',
      labelTranslation: {
        label: 'gift_card.checkout.label.recipient_company_name',
        invalidPatternErrorLabel:
          'gift_card.checkout.error.recipient_company_name_invalid_pattern',
      },
      maxLength: PhysicalGiftCardFieldsMaxLength.COMPANY_NAME,
      syncValidators: [
        Validators.pattern(ONLY_US_ASCII_CHARACTERS),
        Validators.pattern(SPACE_ONLY_REGEX),
      ],
    },
    phoneNumber: {
      className: 'recipient-phone-number',
      key: 'phoneNumber',
      labelTranslation: {
        label: 'gift_card.checkout.label.recipient_phone_number',
        emptyErrorLabel:
          'gift_card.checkout.error.recipient_phone_number_empty',
        invalidPatternErrorLabel:
          'gift_card.checkout.error.recipient_phone_number_invalid_pattern',
      },
      maxLength: PhysicalGiftCardFieldsMaxLength.PHONE_NUMBER,
      syncValidators: [
        Validators.pattern(MOBILE_NUMBER_VALIDATION_REGEX),
        Validators.pattern(SPACE_ONLY_REGEX),
      ],
    },
    email: {
      className: 'recipient-email',
      key: 'email',
      labelTranslation: {
        label: 'gift_card.checkout.label.recipient_email',
        emptyErrorLabel: 'gift_card.checkout.error.recipient_email_empty',
        invalidPatternErrorLabel:
          'gift_card.checkout.error.recipient_email_invalid_pattern',
      },
      disabled: true,
    },
  };

  digitalOnlyFormFields: FieldsetBaseType = {
    title: null,
    elements: [
      { input: this.formFields.firstName },
      { input: this.formFields.lastName },
      { className: 'md:col-span-2', input: this.formFields.email },
    ],
  };

  physicalOnlyFormFields: FieldsetBaseType = {
    title: null,
    elements: [
      { input: this.formFields.firstName },
      { input: this.formFields.lastName },
      { input: this.formFields.phoneNumber },
      { input: this.formFields.companyName },
    ],
  };

  digitalAndPhysicalFormFields: FieldsetBaseType = {
    title: null,
    elements: [
      { input: this.formFields.firstName },
      { input: this.formFields.lastName },
      { className: 'md:col-span-2', input: this.formFields.email },
      { input: this.formFields.phoneNumber },
      { input: this.formFields.companyName },
    ],
  };

  formFieldsFromDeliveryMethods(
    deliveryMethods: DeliveryMethod[],
  ): FieldsetBaseType {
    if (
      deliveryMethods.includes('email') &&
      deliveryMethods.includes('address')
    ) {
      return this.digitalAndPhysicalFormFields;
    } else if (deliveryMethods.includes('address')) {
      return this.physicalOnlyFormFields;
    } else {
      return this.digitalOnlyFormFields;
    }
  }

  preFillData(params: {
    user: User | null;
    formData: Partial<NydusRecipient>;
  }): Record<string, string> {
    const { user, formData } = params;
    const preFilledKeys = [
      'firstName',
      'lastName',
      'email',
      'phoneNumber',
      'companyName',
    ];

    const preFilledDataMap = preFilledKeys.reduce((dataMap, keyName) => {
      if (
        formData?.[keyName as keyof NydusRecipient] &&
        formData?.[keyName as keyof NydusRecipient] !== ''
      ) {
        dataMap.set(keyName, formData[keyName as keyof NydusRecipient]);
      } else if (user?.[keyName as keyof User]) {
        dataMap.set(keyName, user[keyName as keyof User]);
      }

      return dataMap;
    }, new Map());

    // for special key `phone` from User type
    if (!preFilledDataMap.has('phoneNumber') && user?.phone) {
      preFilledDataMap.set('phoneNumber', user.phone);
    }

    const result = {};
    preFilledDataMap.forEach((value, keyName) => {
      // @ts-ignore
      result[keyName] = value;
    });

    return result;
  }
}
